/**
 * Every figure in the app.
 *
 * Monospace with tabular figures, so a column of forty balances aligns to the
 * digit. Color is opt-in and never the only signal — sign-aware coloring
 * always prints an explicit + or −.
 */
import { useCountUp } from '@/lib/motion'
import {
  formatMoney,
  formatMoneyCompact,
  toFlowAmount,
  type Decimal,
  type SignConvention,
} from '@/lib/format'
import { cn } from '@/lib/utils'

export type MoneyTone =
  /**
   * The default. Green when money is yours, red when it is owed or leaving,
   * white at exactly zero — with a printed sign so color is never the only
   * signal. Zero is deliberately white rather than dim: "net zero" is a real
   * answer, and dimming it made it look disabled.
   */
  | 'auto'
  /** Same color as surrounding text. For labels and non-financial figures. */
  | 'neutral'
  /**
   * Money you OWE, passed as a positive number the way a statement prints it.
   * Renders negative and red, so the sign and the color always agree: a card
   * balance of 410 shows as −$410.00.
   */
  | 'owed'
  | 'positive'
  | 'negative'
  | 'dim'

export interface MoneyProps {
  value: Decimal
  /** ISO 4217 code; accounts carry their own. Defaults to USD. */
  currency?: string
  /**
   * `plaid` flips the sign first: in raw transaction rows a positive amount is
   * money *leaving* the account (D18). Aggregate endpoints are already
   * normalized, so leave this alone for them.
   */
  convention?: SignConvention
  tone?: MoneyTone
  /** `$1.2K` instead of `$1,248.30` — for axis ticks and cramped columns. */
  compact?: boolean
  /** Drop the cents. Use for headline figures, never for a payable amount. */
  whole?: boolean
  /** Force a leading + on positives. Implied by `tone="auto"`. */
  showSign?: boolean
  /** Count up on first paint. Reserve this for a page's headline figure. */
  animate?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  /** Printed when the value is absent. */
  fallback?: string
}

const SIZES: Record<NonNullable<MoneyProps['size']>, string> = {
  xs: 'text-[0.6875rem]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg',
  xl: 'text-3xl sm:text-4xl',
}

export function Money({
  value,
  currency,
  convention = 'natural',
  tone = 'auto',
  compact = false,
  whole = false,
  showSign = false,
  animate = false,
  size = 'md',
  className,
  fallback,
}: MoneyProps) {
  // `owed` takes a statement-style positive and states it as what it does to
  // the household: a liability is negative money.
  const raw = toFlowAmount(value, convention)
  const amount = tone === 'owed' && raw !== null ? -Math.abs(raw) : raw
  const animated = useCountUp(animate ? amount : null)
  const shown = animate && animated !== null ? animated : amount

  const signDisplay = tone === 'auto' || tone === 'owed' || showSign ? 'exceptZero' : 'auto'
  const print = (input: number | null) =>
    compact
      ? formatMoneyCompact(input, { currency, signDisplay, fallback })
      : formatMoney(input, { currency, decimals: whole ? 0 : 2, signDisplay, fallback })

  const isCounting = animate && shown !== amount

  return (
    <span
      className={cn('tabular whitespace-nowrap', SIZES[size], toneClass(tone, amount), className)}
      // Assistive tech gets the settled figure, never a frame of the count-up.
      aria-label={isCounting ? print(amount) : undefined}
    >
      <span aria-hidden={isCounting || undefined}>{print(shown)}</span>
    </span>
  )
}

function toneClass(tone: MoneyTone, amount: number | null): string {
  switch (tone) {
    case 'positive':
      return 'text-mint'
    case 'negative':
      return 'text-rose'
    case 'dim':
      return 'text-dim'
    case 'owed':
    case 'auto':
      if (amount === null) return 'text-dim'
      // Exactly zero is white — squared up, nothing owed, nothing owing.
      if (amount === 0) return 'text-foreground'
      return amount > 0 ? 'text-mint hud-glow-mint' : 'text-rose hud-glow-rose'
    case 'neutral':
    default:
      return ''
  }
}
